import React, { forwardRef, KeyboardEvent, MouseEvent, PropsWithChildren, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ANIMATION_DURATION_MS, bemClassNames, combineRefs, invariant, Key, stopPropagationHandler, TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "@multiweb/reactUtils";
import { ForwardRefType, MouseButtons } from "@multiweb/types/react";
import { Role } from "@multiweb/uiShared/Role";
import { MODAL_ROOT_ID } from "../../constants";
import { AnimatedMount, AnimationType } from "../AnimatedMount";
import { ResizeOptionsProps } from "../types";
import { clampWindowDimension, getFittableSize, isWindowOutOfBounds, setInitialPosition } from "./utils";

export type ModalWindowProps = PropsWithChildren<{
    /** přepínač určující, zda-li se modal window bude moci zavřít i při kliknutí mimo něj */
    readonly closeByOuterClick?: boolean;
    /** přepinač určující, zda-li se modal window bude moci posouvat */
    readonly draggable?: boolean;
    /** přepínač určující, zda-li bude mít modal window průhledné pozadí nebo klasické MW (backdrop) */
    readonly hasTransparentBackdrop?: boolean;
    /** zobrazení modal window */
    readonly isDisplayed: boolean;
    /** pokud je true, tak skryje modální okno, ale nepřestane jej renderovat v DOM-u */
    readonly isHidden?: boolean;
    /** Callback volaný při stisku klávesy ESCAPE. Předání hodnoty také způsobí, že při stisku ESCAPE se modální okno zavře. */
    readonly closeOnEscape?: ((e: KeyboardEvent<HTMLDivElement>) => void) | true;
    /** callback funkce volaná po dokončení animace skrytí modálního okna */
    readonly onCloseChange: () => void;
    readonly onOuterClick?: () => void;
    /** Id elementu pro přístupnost aria-labelledby */
    readonly labelId?: string;
    /** Funkce volaná při zaměření modálu. To nastává při prvním renderu komponenty nebo když uživatel z modálu focusem odjede a pak se na něj vrátí. */
    readonly onContentFocus?: () => void;
}> & ResizeOptionsProps;

const classNames = bemClassNames("modal-component");
const box = classNames("box");
const draggingLineContainer = classNames("dragging-line-container");
const resizerClass = classNames("resizer");
const resizerAreaClass = classNames("resizer-area");

/** hodnota pro zarovnání modal okna směrem na střed - nahrazuje css vlastnost: left: 50% */
const ALIGN_LEFT = 0.50;
/** hodnota pro zarovnání modal okna směrem na střed - nahrazuje css vlastnost: top: 47% (opticky lepší nedávat okno úplně na střed) */
const ALIGN_TOP = 0.47;
/** šířka resizeru */
const RESIZER_THICKNESS = 8;

export const ModalWindow: ForwardRefType<HTMLDivElement, ModalWindowProps> = forwardRef<HTMLDivElement, ModalWindowProps>(({
    children,
    draggable = false,
    resizable = false,
    asymmetricallyResizable = false,
    isDisplayed = true,
    isHidden = false,
    closeByOuterClick = false,
    hasTransparentBackdrop = false,
    labelId,
    closeOnEscape,
    onOuterClick,
    onCloseChange,
    onContentFocus,
    defaultHeight,
    defaultWidth,
    minWidth = 200,
    maxWidth,
    minHeight = 200,
    maxHeight,
}, forwardRef) => {
    const { innerWidth: browserWidth, innerHeight: browserHeight } = window;
    const [windowDimensions, setWindowDimensions] = useState({ width: getFittableSize(browserWidth, defaultWidth), height: getFittableSize(browserHeight, defaultHeight) });
    const [windowPosition, setWindowPosition] = useState({ left: setInitialPosition(browserWidth, ALIGN_LEFT, windowDimensions.width), top: setInitialPosition(browserHeight, ALIGN_TOP, windowDimensions.height) });
    const [isDraggerActive, setIsDraggerActive] = useState(false);
    const [closingRequested, setClosingRequested] = useState(false);
    const isDragging = useRef(false);
    const isResizing = useRef(false);
    const isClosing = useRef(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const initialCoordinates = useRef({ x: 0, y: 0 });
    const clickStartedFromWithin = useRef<boolean>(false);
    const backgroundRef = useRef<HTMLDivElement>(null);
    const combinedBackgroundRef = combineRefs(backgroundRef, forwardRef);

    const modalRoot = document.getElementById(MODAL_ROOT_ID);

    invariant(modalRoot, `[ModalWindow] Cannot render modal window because element with id ${MODAL_ROOT_ID} is undefined.`);

    useEffect(() => {
        // Tento useEffect zajistí, že po otevření okna se focus dá na okno.
        // Pokud ale programátor v obsahu okna už nějakému prvku nastavil focus, tak se tento kód neprovede.
        if (backgroundRef.current && !backgroundRef.current.contains(document.activeElement))
            backgroundRef.current.focus();
    }, []);

    useEffect(() => {
        if ((!isDisplayed || closingRequested) && !isClosing.current) {
            isClosing.current = true;
            setTimeout(onCloseChange, ANIMATION_DURATION_MS);
        }
    }, [isDisplayed, closingRequested, onCloseChange]);

    const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
        stopPropagationHandler(event);

        // reagujeme na onKeyUp, aby se při zavření ModalWindow neodchytil stisk klávesy na nějakém jiném elementu, který se po zavření zaměří.
        if (event.key === Key.Escape && closeOnEscape) {
            if (typeof closeOnEscape === "function") {
                closeOnEscape(event);
            }
            setClosingRequested(true);
        }
    };

    const handleOuterClick = (e: MouseEvent<HTMLDivElement>) => {
        if (closeByOuterClick && e.currentTarget === e.target && !clickStartedFromWithin.current)
            !onOuterClick ? setClosingRequested(true) : onOuterClick();
    };

    const onDraggerEnter = () => {
        setIsDraggerActive(true);
    };

    const onDraggerLeave = () => {
        setIsDraggerActive(false);
    };

    const handleDragStart = (e: MouseEvent<HTMLDivElement>) => {
        isDragging.current = true;
        initialCoordinates.current = { x: e.clientX, y: e.clientY };
        e.stopPropagation();
    };

    const handleResizeStart = (e: MouseEvent<HTMLDivElement>) => {
        isResizing.current = true;
        e.stopPropagation();
    };

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
        if (e.buttons === MouseButtons.leftButton && isDragging.current)
            handleDragMove(e);
        else if (e.buttons === MouseButtons.leftButton && isResizing.current)
            handleResizeMove(e);
        else {
            isDragging.current = false;
            isResizing.current = false;
        }
    };

    const handleDragMove = (e: MouseEvent<HTMLDivElement>) => {
        const deltaX = e.clientX - initialCoordinates.current.x;
        const deltaY = e.clientY - initialCoordinates.current.y;
        const newLeft = windowPosition.left + deltaX;
        const newTop = windowPosition.top + deltaY;

        if (isWindowOutOfBounds(newLeft, newTop, windowDimensions.width, browserWidth, browserHeight))
            return;

        setWindowPosition({ left: newLeft, top: newTop });
        initialCoordinates.current = { x: e.clientX, y: e.clientY };
    };

    const handleResizeMove = (e: MouseEvent<HTMLDivElement>) => {
        if (contentRef.current === null)
            return;

        const contentBcr = contentRef.current.getBoundingClientRect();
        const deltaWidth = e.clientX - contentBcr.right + RESIZER_THICKNESS;
        const deltaHeight = e.clientY - contentBcr.bottom + RESIZER_THICKNESS;

        if (e.clientX >= contentBcr.left && e.clientY >= contentBcr.top) {
            const newWidth = windowDimensions.width + (resizable ? deltaWidth * 2 : deltaWidth);
            const newHeight = windowDimensions.height + (windowPosition.top !== 0 && resizable ? deltaHeight * 2 : deltaHeight);

            const clampWidth = clampWindowDimension(newWidth, minWidth, browserWidth);
            const clampHeight = clampWindowDimension(newHeight, minHeight, browserHeight);

            setWindowDimensions({
                width: clampWidth,
                height: clampHeight,
            });

            if (resizable) {
                const newLeft = windowPosition.left - (clampWidth - windowDimensions.width) / 2;
                const newTop = Math.max(0, windowPosition.top - (clampHeight - windowDimensions.height) / 2);

                setWindowPosition({
                    left: newLeft,
                    top: newTop,
                });
            }
        }
    };

    const handleDragStop = (e: MouseEvent<HTMLDivElement>) => {
        isDragging.current = false;
        clickStartedFromWithin.current = contentRef?.current?.contains(e.target as Element) === true;
    };

    const handleResizeStop = () => {
        isResizing.current = false;
    };

    const handleModalCenter = (e: MouseEvent<HTMLDivElement>) => {
        if (e.currentTarget === e.target)
            setWindowPosition({
                left: setInitialPosition(browserWidth, ALIGN_LEFT, windowDimensions.width),
                top: setInitialPosition(browserHeight, ALIGN_TOP, windowDimensions.height),
            });
    };

    /** Ošetřuje, aby se focus nikdy nedostal na samotný modál/backdrop. */
    const onModalFocus = (e: React.FocusEvent<HTMLDivElement>) => {
        if (!backgroundRef.current) {
            return;
        }

        // Pokud se focus snaží dostat přímo na modál (= neprobublalo to jen z obsahu komponenty).
        if (e.target.isSameNode(backgroundRef.current)) {
            /** Informace, zda událost focusu byla vyvolána v rámci obsahu komponenty. */
            const focusFromInside = !!e.relatedTarget && backgroundRef.current.contains(e.relatedTarget as Node);

            // Pokud se klávesou TAB podařilo skočit z obsahu na modál, tak focus vrátíme na poslední zaměřený element z obsahu.
            if (focusFromInside) {
                (e.relatedTarget as HTMLElement).focus();
            } else {
                // Pokud bylo vyvoláno zaměření od někud mimo obsah modálu, tak zaměříme obsah modálu.
                onContentFocus?.();
            }
        }

        e.stopPropagation();
    };

    return (createPortal(
        <div
            onKeyUp={handleKeyUp}
            onFocus={onModalFocus}
            tabIndex={TAB_INDEX_FOCUSABLE_TAB_SEQUENCE} // Nastavujeme tabIndex, aby se vyvolávala metoda onFocus a my mohli focus zpracovat. Samotný modál by ale nikdy zaměřen být neměl.
            ref={combinedBackgroundRef}
            className={classNames({ "hidden": isHidden })}
        >
            <AnimatedMount animationDurationMs={ANIMATION_DURATION_MS} animationType={AnimationType.opacity} mounted={isDisplayed && !closingRequested}>
                <div className={classNames("backdrop", { "transparent-backdrop": hasTransparentBackdrop })} onClick={handleOuterClick} onMouseMove={handleMouseMove} onMouseDown={handleDragStop} onMouseUp={handleResizeStop} onDoubleClick={handleModalCenter}>
                    <div ref={contentRef} className={box} style={{ width: windowDimensions.width, height: windowDimensions.height, minWidth, minHeight, maxWidth, maxHeight, left: windowPosition.left, top: windowPosition.top }} role={Role.dialog} aria-labelledby={labelId} >
                        {draggable &&
                            <div className={draggingLineContainer} onMouseDown={handleDragStart} onMouseUp={handleDragStop} onMouseEnter={onDraggerEnter} onMouseLeave={onDraggerLeave}>
                                <div className={classNames("dragging-line", { "active": isDraggerActive })} />
                            </div>
                        }
                        {(resizable || asymmetricallyResizable) &&
                            <div className={resizerAreaClass} onMouseDown={handleResizeStart}>
                                <div className={resizerClass} />
                            </div>
                        }
                        {children}
                    </div>
                </div>
            </AnimatedMount>
        </div>, modalRoot)
    );
});
